import React, { useState } from 'react';

// Interface for email record data
export interface EmailRecord {
  campaign: string; // Campaign name
  fromEmail: string; // Sender email
  sentAt: string; // Sent date
  status: 'Delivered' | 'Not Delivered' | 'Bounced'; // Email status
}

// Props interface for EmailInitiatedTable component
interface EmailInitiatedTableProps {
  records: EmailRecord[]; // Array of email records
  itemsPerPage?: number; // Optional number of items per page (default: 6)
  emptyMessage?: string; // Custom message when no records
}

// Component to display a paginated table of email campaign records
const EmailInitiatedTable: React.FC<EmailInitiatedTableProps> = ({
  records,
  itemsPerPage = 6,
  emptyMessage = 'No email history found',
}) => {
  // State for current page
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil(records.length / itemsPerPage);

  // Calculate records for current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = records.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className='bg-white rounded-lg border border-gray-200 shadow-sm mt-6 p-6'>
      {/* Table Header */}
      <h3 className='text-[18px] font-semibold mb-3 text-[#3F5265]'>
        Email Initiated
      </h3>

      {records.length === 0 ? (
        // Empty state with "No data" message
        <div className='flex justify-center items-center py-12'>
          <div className='text-center'>
            <p className='text-gray-500 text-base'>{emptyMessage}</p>
            <p className='text-gray-400 text-sm mt-1'>No email campaigns have been sent to this contact yet</p>
          </div>
        </div>
      ) : (
        <>
          <table className='w-full border text-sm'>
            <thead className='bg-gray-50 text-[#657584] text-[12px]'>
              <tr>
                <th className='px-6 py-4 text-left font-normal'>CAMPAIGNS</th>
                <th className='px-6 py-4 text-left font-normal'>FROM EMAIL</th>
                <th className='px-6 py-4 text-left font-normal'>SENT AT</th>
                <th className='px-6 py-4 text-left font-normal'>STATUS</th>
              </tr>
            </thead>
            <tbody className='text-[#1F2937] text-[14px]'>
              {currentRecords.map((record, index) => (
                <tr key={index} className='border-t'>
                  <td className='px-6 py-4'>
                    {record.campaign}
                    {/* <div className='text-xs text-gray-400'>{'Subject line:'}</div> */}
                  </td>
                  <td className='px-6 py-4'>{record.fromEmail}</td>
                  <td className='px-6 py-4'>{record.sentAt}</td>
                  <td
                    className={`px-6 py-4 ${record.status === 'Delivered'
                      ? 'text-green-600'
                      : record.status === 'Not Delivered'
                        ? 'text-red-500'
                        : 'text-[#ED900F]'
                      }`}
                  >
                    <span
                      className={`px-3 py-1 rounded-full ${record.status === 'Delivered'
                        ? 'bg-[#ECFDF5]'
                        : record.status === 'Not Delivered'
                          ? 'bg-[#FCECEC]'
                          : 'bg-[#FFF7E6]'
                        } rounded-[40px]`}
                    >
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {records.length > itemsPerPage && (
            <div className='flex justify-end mt-4 gap-2'>
              <button
                className='px-3 py-1 border rounded disabled:opacity-50'
                onClick={() => setCurrentPage((prev) => prev - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  className={`px-3 py-1 border rounded ${currentPage === idx + 1
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-600'
                    }`}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                className='px-3 py-1 border rounded disabled:opacity-50'
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailInitiatedTable;